import { useEffect, useState } from "react";

import { Filter, SortAscending } from "@carbon/icons-react";
import toast from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMMON_MESSAGES } from "@/utils/message";

import { Loader2 } from "lucide-react";
import { getGroupRootGroupDeleted } from "../../api/group.api";
import GroupCardRestore from "../../components/GroupCard/GroupCardRestore";
import SearchModal from "../../components/SearchModal/SearchModal";
import {
  GroupSortDateEnum,
  GroupSortEnum,
} from "../../constants/group.constant";
import { GroupPathsEnum } from "../../constants/group.paths";
import { RestoreGroup } from "../../types/group.type";

const RootGroupDeletedScreen = () => {
  const [myGroupsRestore, setMyGroupsRestore] = useState<RestoreGroup[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 9;
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [totalElement, setTotalElement] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const [sort, setSort] = useState<string | null>(null);

  // Update filter states when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    setSort(urlParams.get("sort"));
  }, [location.search]);

  let isFiltering = false;
  if (sort) {
    isFiltering = true;
  }

  const fetchMyGroupsInfiniteScroll = async (pageScroll: number) => {
    try {
      setIsLoadMore(true);
      const result = await getGroupRootGroupDeleted({
        pageNumber: pageScroll,
        pageSize: pageSize,
        sort: sort as GroupSortEnum,
      });
      if (!result.data.length || result.data.length < pageSize) {
        setIsLoadMore(false);
      }
      setMyGroupsRestore(() => [...myGroupsRestore, ...result.data]);
    } catch (error) {
      toast.error(COMMON_MESSAGES.MSG_012);
    }
  };

  const fetchMyGroups = async (pageNumber: number) => {
    try {
      setLoading(true);
      const result = await getGroupRootGroupDeleted({
        pageNumber: pageNumber,
        pageSize: pageSize,
        sort: sort as GroupSortEnum,
      });
      setTotalElement(result.totalElements || 0);
      if (!result.data.length || result.data.length < pageSize) {
        setIsLoadMore(false);
      }
      setMyGroupsRestore(result.data);
    } catch (error) {
      toast.error(COMMON_MESSAGES.MSG_012);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchMyGroups(page);
  }, [sort]);

  useEffect(() => {
    fetchMyGroupsInfiniteScroll(page);
  }, []);

  return (
    <div className="min-h-screen bg-secondary-25">
      {/* Enhanced Header with Integrated Filters */}
      <div className="bg-white sticky top-[70px] z-40">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Main Header Row */}
          <div className="py-4 border-b border-secondary-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-secondary-900">
                    Lưu trữ
                  </h1>
                  <p className="text-sm text-secondary-600 mt-1">
                    Quản lý các Tổ chức/Nhóm đã xóa thuộc quyền sở hữu
                  </p>
                </div>
                <Badge
                  variant="secondary"
                  className="bg-primary-50 text-primary-700 px-2 py-1 text-xs font-semibold"
                >
                  {totalElement}
                </Badge>
              </div>
              <div className="flex items-center space-x-3">
                <SearchModal />
              </div>
            </div>
          </div>

          {/* Quick Filters Row */}
          <div className="py-3">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              {/* Quick Filter Pills */}
              <div className="flex items-center gap-3">
                <div className="flex items-center space-x-2">
                  <Filter
                    size={16}
                    className="text-secondary-500"
                  />
                  <span className="text-sm font-medium text-secondary-700">
                    Lọc nhanh:
                  </span>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* All Archive Pill */}
                  <button
                    onClick={() => {
                      setSort(null);
                      setPage(1);
                      navigate(`${window.location.pathname}`);
                    }}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-200 ${
                      !isFiltering
                        ? "bg-primary-500 text-white border-primary-500 shadow-sm"
                        : "bg-white text-secondary-600 border-secondary-200 hover:border-secondary-300 hover:bg-secondary-50"
                    }`}
                  >
                    Tất cả lưu trữ
                  </button>
                </div>
              </div>

              {/* Advanced Filters */}
              <div className="flex items-center gap-2">
                {/* Sort Dropdown */}
                <Select
                  value={sort || GroupSortDateEnum.NEWEST}
                  onValueChange={(value) => {
                    const urlParams = new URLSearchParams(location.search);
                    if (value === GroupSortDateEnum.NEWEST) {
                      setSort(null);
                      urlParams.delete("sort");
                    } else {
                      urlParams.set("sort", value);
                      setPage(1);
                      setSort(value);
                    }
                    navigate(
                      `${window.location.pathname}?${urlParams.toString()}`
                    );
                  }}
                >
                  <SelectTrigger className="w-auto min-w-[120px] h-8 text-xs border-secondary-200 focus:border-primary-300 hover:border-primary-200 transition-colors bg-white">
                    <SortAscending
                      size={14}
                      className="mr-1 text-secondary-500"
                    />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value={GroupSortDateEnum.NEWEST}>
                        Mới nhất
                      </SelectItem>
                      <SelectItem value={GroupSortDateEnum.OLDEST}>
                        Cũ nhất
                      </SelectItem>
                    </SelectGroup>
                  </SelectContent>
                </Select>

                {/* Clear Filters */}
                {isFiltering && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSort(null);
                      setPage(1);
                      navigate(`${window.location.pathname}`);
                    }}
                    className="h-8 px-2 text-xs text-secondary-500 hover:text-secondary-700 hover:bg-secondary-50"
                  >
                    <svg
                      className="w-3 h-3"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          </div>
        ) : (
          <>
            {/* Filter Loading Indicator */}
            {loading && isFiltering && (
              <div className="flex items-center justify-center py-4 mb-6">
                <div className="flex items-center space-x-2 text-secondary-600">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm">Đang áp dụng bộ lọc...</span>
                </div>
              </div>
            )}

            <InfiniteScroll
              style={{
                height: "unset",
                overflow: "unset",
              }}
              dataLength={myGroupsRestore.length}
              next={() => {
                setPage((prev) => prev + 1);
                fetchMyGroupsInfiniteScroll(page + 1);
              }}
              hasMore={isLoadMore}
              loader={
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                </div>
              }
            >
              {myGroupsRestore.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="max-w-md">
                    <h3 className="text-xl font-semibold text-secondary-900 mt-6 mb-3">
                      {isFiltering
                        ? "Không tìm thấy nhóm đã xóa nào"
                        : "Chưa có nhóm nào được lưu trữ"}
                    </h3>
                    <p className="text-secondary-600 mb-8 leading-relaxed">
                      {isFiltering
                        ? "Thử điều chỉnh bộ lọc để tìm thấy nhóm phù hợp"
                        : "Các Tổ chức/Nhóm đã xóa sẽ xuất hiện tại đây và có thể được khôi phục."}
                    </p>

                    {/* Action buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      {isFiltering ? (
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSort(null);
                            setPage(1);
                            navigate(`${window.location.pathname}`);
                          }}
                          className="text-primary-600 border-primary-600 hover:bg-primary-50"
                        >
                          Xóa tất cả bộ lọc
                        </Button>
                      ) : (
                        <Link
                          to={GroupPathsEnum.GROUP_DISCOVER}
                          className="inline-flex items-center justify-center px-6 py-3 text-sm font-medium text-white bg-primary-600 border border-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                        >
                          Quản lý nhóm
                        </Link>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {myGroupsRestore.map((group) => (
                    <GroupCardRestore
                      key={group.id}
                      group={group}
                    />
                  ))}
                </div>
              )}
            </InfiniteScroll>
          </>
        )}
      </div>
    </div>
  );
};

export default RootGroupDeletedScreen;
